import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { formatIndianCurrency } from '../utils/currency';
import { PlanConfirmDialog } from '../components/subscription/PlanConfirmDialog';
import api from '../utils/api';
import { 
  Check, Crown, Zap, Building2, Users, FileText, 
  Shield, Sparkles, Calendar, CreditCard, AlertCircle,
  ArrowRight, Loader2, Banknote, Receipt, Clock, 
  CheckCircle2, XCircle, TrendingUp, Database, Route,
  Hotel, UserPlus, Settings, BarChart3, FileSpreadsheet,
  Send, Percent, Package, Layers, Copy, Download,
  Monitor, Smartphone, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature icons mapping
const FEATURE_ICONS = {
  quotation_engine: Package,
  quotes_per_month: FileText,
  hotel_slots: Hotel,
  b2b_agent_portal: UserPlus,
  staff_logins: Users,
  multi_category: Layers,
  dual_meal_plan: Percent,
  whatsapp_dispatch: Send,
  transfer_manager: Route,
  season_aware_rates: Calendar,
  itinerary_master: FileSpreadsheet,
  gst_configuration: Settings,
  pipeline_dashboard: BarChart3,
  auto_doc_generation: FileText,
  quote_terms: FileText,
  person_wise_format: Users,
  misc_cost_lines: Database,
  hotels_by_category: Hotel,
  route_slots: Route,
};

// Bank Details Configuration
const BANK_DETAILS = {
  bank: 'Kotak Mahindra Bank',
  accountName: 'Trip Showcase Pvt Ltd',
  accountNo: '7812889960',
  ifscCode: 'KKBK0001767',
  upiId: 'tripshowcase@kotak'
};

const SubscriptionPage = () => {
  const { tenant, tenantSlug } = useTenant();
  const { refreshFeatures } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [processingPlan, setProcessingPlan] = useState(null);
  const [showTrialDialog, setShowTrialDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showPaidConfirm, setShowPaidConfirm] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showFeaturesDialog, setShowFeaturesDialog] = useState(false);
  const [featuresDialogPlan, setFeaturesDialogPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedTrialDuration, setSelectedTrialDuration] = useState(14);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('plans');
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [paymentForm, setPaymentForm] = useState({
    reference_no: '',
    payment_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    notes: ''
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes, invoicesRes] = await Promise.all([
        api.get('/subscription/plans'),
        api.get('/subscription/my').catch(() => ({ data: null })),
        api.get('/subscription/invoices').catch(() => ({ data: [] }))
      ]);
      
      // Only advertise features that are switched ON in the plan's configuration
      setPlans((plansRes.data || []).map((p) => ({ ...p, features: (p.features || []).filter((f) => f.is_enabled) })));
      setCurrentSubscription(subRes.data);
      refreshFeatures();
      setInvoices(invoicesRes.data || []);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (plan) => {
    return billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
  };

  const getMonthlyEquivalent = (plan) => {
    if (billingCycle === 'annual') {
      return Math.round(plan.price_annual / 12);
    }
    return plan.price_monthly;
  };

  const openTrialDialog = (plan) => {
    setSelectedPlan(plan);
    setSelectedTrialDuration(plan.trial_durations?.[0] || 14);
    setShowTrialDialog(true);
  };

  const openPaymentDialog = (plan) => {
    setSelectedPlan(plan);
    setPaymentMethod('online');
    setPaymentForm({
      reference_no: '',
      payment_date: new Date().toISOString().split('T')[0],
      bank_name: '',
      notes: ''
    });
    setShowPaymentDialog(true);
  };

  const handleStartTrial = async () => {
    if (!selectedPlan) return;
    
    setProcessingPlan(selectedPlan.slug);
    
    try {
      await api.post('/subscription/trial', {
        plan_slug: selectedPlan.slug,
        duration_days: selectedTrialDuration
      });
      
      setShowTrialDialog(false);
      setShowSuccessDialog(true);
      await fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to start trial';
      toast.error(errorMsg);
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedPlan) return;
    
    setSubmittingPayment(true);
    
    try {
      // Create subscription with payment details
      const response = await api.post('/subscription/subscribe', {
        plan_slug: selectedPlan.slug,
        billing_cycle: billingCycle,
        payment_method: paymentMethod === 'online' ? 'razorpay' : 'bank_transfer',
        payment_details: paymentMethod === 'offline' ? {
          reference_no: paymentForm.reference_no,
          payment_date: paymentForm.payment_date,
          bank_name: paymentForm.bank_name,
          notes: paymentForm.notes
        } : null
      });
      
      setShowPaymentDialog(false);
      setShowPaidConfirm(false);
      
      if (paymentMethod === 'offline') {
        toast.success('Payment details submitted! Your subscription will be activated within 24-48 hours after verification.');
      } else {
        toast.success('Subscription activated successfully!');
      }
      
      await fetchData();
      setActiveTab('invoices');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to process payment';
      toast.error(errorMsg);
    } finally {
      setSubmittingPayment(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`/subscription/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getPlanIcon = (slug) => {
    switch (slug) {
      case 'starter': return Zap;
      case 'growth': return TrendingUp;
      case 'pro': return Crown;
      case 'enterprise': return Building2;
      default: return Sparkles;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Trial</Badge>;
      case 'past_due':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Past Due</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-100 text-slate-700"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{status || 'No Subscription'}</Badge>;
    }
  };

  const getInvoiceStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const isCurrentPlan = (slug) => {
    return currentSubscription?.plan?.slug === slug && 
           ['active', 'trial'].includes(currentSubscription?.status);
  };

  const canStartTrial = (plan) => {
    // Can start trial if no subscription or subscription is expired/cancelled
    if (!currentSubscription || currentSubscription.status === 'no_subscription') {
      return plan.trial_available;
    }
    return false;
  };

  const formatLimit = (limit) => {
    if (limit === null || limit === -1) return 'Unlimited';
    return limit.toLocaleString();
  };

  const calculatePrice = (plan) => {
    const basePrice = getPrice(plan);
    const gst = Math.round(basePrice * 0.18);
    return { basePrice, gst, total: basePrice + gst };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8" data-testid="subscription-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Subscription & Billing</h1>
          <p className="text-slate-600 mt-2">
            Manage your plan and view billing history
          </p>
        </div>

        {/* Current Subscription Status */}
        {currentSubscription && currentSubscription.status !== 'no_subscription' && (
          <Card className={`border-2 ${currentSubscription.status === 'active' ? 'border-green-200 bg-green-50' : currentSubscription.status === 'trial' ? 'border-blue-200 bg-blue-50' : 'border-slate-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${currentSubscription.status === 'active' ? 'bg-green-500' : 'bg-blue-500'}`}>
                    {currentSubscription.status === 'trial' ? (
                      <Clock className="w-6 h-6 text-white" />
                    ) : (
                      <Check className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-lg text-slate-900">
                        {currentSubscription.plan?.name || 'Unknown Plan'}
                      </p>
                      {getStatusBadge(currentSubscription.status)}
                      {currentSubscription.plan?.is_discontinued && (
                        <Badge variant="outline" className="border-amber-400 text-amber-700" data-testid="plan-discontinued-badge">
                          Plan discontinued
                        </Badge>
                      )}
                    </div>
                    {currentSubscription.status === 'trial' && currentSubscription.trial_ends_at && (
                      <p className="text-sm text-blue-700">
                        Trial ends: {new Date(currentSubscription.trial_ends_at).toLocaleDateString()}
                      </p>
                    )}
                    {currentSubscription.status === 'active' && currentSubscription.ends_at && (
                      <p className="text-sm text-green-700">
                        Renews: {new Date(currentSubscription.ends_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                {currentSubscription.billing_cycle && (
                  <Badge variant="outline" className="border-slate-400">
                    {currentSubscription.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} Billing
                  </Badge>
                )}
              </div>

              {/* Usage Summary */}
              {currentSubscription.features && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                  {['quotes_per_month', 'hotel_slots', 'b2b_agent_portal', 'staff_logins', 'route_slots'].map((key) => {
                    const feature = currentSubscription.features[key];
                    if (!feature) return null;
                    const Icon = FEATURE_ICONS[key] || Database;
                    const used = feature.used || 0;
                    const limit = feature.limit;
                    const isUnlimited = limit === null || limit === -1;
                    const percentage = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
                    
                    return (
                      <div key={key} className="bg-white/60 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 text-slate-500" />
                          <span className="text-xs text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg font-semibold">{used}</span>
                          <span className="text-sm text-slate-500">/ {formatLimit(limit)}</span>
                        </div>
                        {!isUnlimited && (
                          <Progress value={percentage} className="h-1.5 mt-2" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto">
            <TabsTrigger value="plans">Plans</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
          </TabsList>

          {/* Plans Tab */}
          <TabsContent value="plans" className="mt-6">
            {/* Billing Cycle Toggle */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-3 p-1 bg-slate-100 rounded-lg">
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingCycle === 'monthly' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  onClick={() => setBillingCycle('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    billingCycle === 'annual' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  onClick={() => setBillingCycle('annual')}
                >
                  Annual
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                    ~17% off
                  </Badge>
                </button>
              </div>
            </div>

            {/* Plans Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const Icon = getPlanIcon(plan.slug);
                const isCurrent = isCurrentPlan(plan.slug);
                const isProcessing = processingPlan === plan.slug;
                const isPopular = plan.slug === 'growth';
                
                return (
                  <Card 
                    key={plan.id}
                    className={`relative ${
                      isPopular 
                        ? 'border-indigo-500 border-2 shadow-lg' 
                        : 'border-slate-200'
                    } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
                    data-testid={`plan-card-${plan.slug}`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-indigo-500 text-white">Most Popular</Badge>
                      </div>
                    )}
                    
                    {isCurrent && (
                      <div className="absolute -top-3 right-4">
                        <Badge className="bg-green-500 text-white">Current Plan</Badge>
                      </div>
                    )}

                    <CardHeader className="text-center pb-2">
                      <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-4 ${
                        isPopular ? 'bg-indigo-100' : 'bg-slate-100'
                      }`}>
                        <Icon className={`w-7 h-7 ${isPopular ? 'text-indigo-600' : 'text-slate-600'}`} />
                      </div>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <CardDescription>{plan.description}</CardDescription>
                    </CardHeader>

                    <CardContent className="text-center">
                      <div className="mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-slate-900">
                            {formatIndianCurrency(getMonthlyEquivalent(plan) / 100, true, 0)}
                          </span>
                          <span className="text-slate-500">/month</span>
                        </div>
                        {billingCycle === 'annual' && (
                          <p className="text-sm text-slate-500 mt-1">
                            Billed {formatIndianCurrency(getPrice(plan) / 100)} annually
                          </p>
                        )}
                      </div>

                      <Separator className="my-4" />

                      <ul className="space-y-3 text-left">
                        {plan.features?.slice(0, 6).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">
                              {feature.name}
                              {feature.limit_value !== null && feature.limit_value !== -1 && (
                                <span className="text-slate-400 ml-1">
                                  ({feature.limit_value})
                                </span>
                              )}
                              {feature.limit_value === -1 && (
                                <span className="text-green-600 ml-1">(Unlimited)</span>
                              )}
                            </span>
                          </li>
                        ))}
                        {plan.features?.length > 6 && (
                          <li 
                            className="text-sm text-indigo-600 font-medium cursor-pointer hover:underline"
                            onClick={() => {
                              setFeaturesDialogPlan(plan);
                              setShowFeaturesDialog(true);
                            }}
                          >
                            +{plan.features.length - 6} more features
                          </li>
                        )}
                      </ul>
                    </CardContent>

                    <CardFooter className="flex-col gap-2 pt-4 border-t">
                      {isCurrent ? (
                        <Button className="w-full" variant="outline" disabled>
                          Current Plan
                        </Button>
                      ) : canStartTrial(plan) ? (
                        <>
                          <Button
                            variant={isPopular ? undefined : "secondary"}
                            className={`w-full ${
                              isPopular 
                                ? '!bg-indigo-600 hover:!bg-indigo-700 text-white' 
                                : ''
                            }`}
                            disabled={isProcessing}
                            onClick={() => openTrialDialog(plan)}
                            data-testid={`trial-btn-${plan.slug}`}
                          >
                            {isProcessing ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                            ) : (
                              <>Start Free Trial</>
                            )}
                          </Button>
                          <Button 
                            variant="outline" 
                            className="w-full border-slate-300"
                            onClick={() => openPaymentDialog(plan)}
                            data-testid={`subscribe-now-btn-${plan.slug}`}
                          >
                            Subscribe Now
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant={isPopular ? undefined : "secondary"}
                          className={`w-full ${
                            isPopular 
                              ? '!bg-indigo-600 hover:!bg-indigo-700 text-white' 
                              : ''
                          }`}
                          disabled={isProcessing}
                          onClick={() => openPaymentDialog(plan)}
                          data-testid={`subscribe-btn-${plan.slug}`}
                        >
                          {isProcessing ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                          ) : (
                            <>
                              Subscribe Now
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </>
                          )}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Feature Access</CardTitle>
                <CardDescription>
                  Features and limits based on your current plan
                </CardDescription>
              </CardHeader>
              <CardContent>
                {currentSubscription?.features ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(currentSubscription.features).map(([key, feature]) => {
                      const Icon = FEATURE_ICONS[key] || Settings;
                      const isEnabled = feature.enabled;
                      
                      return (
                        <div 
                          key={key}
                          className={`p-4 rounded-lg border ${isEnabled ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isEnabled ? 'bg-green-100' : 'bg-slate-200'}`}>
                              <Icon className={`w-5 h-5 ${isEnabled ? 'text-green-600' : 'text-slate-400'}`} />
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${isEnabled ? 'text-green-900' : 'text-slate-500'}`}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </p>
                              {isEnabled && feature.limit !== undefined && (
                                <p className="text-sm text-slate-500">
                                  {feature.used !== undefined && `${feature.used} / `}
                                  {formatLimit(feature.limit)}
                                </p>
                              )}
                            </div>
                            {isEnabled ? (
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-slate-300" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Subscribe to a plan to unlock features</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Billing History</CardTitle>
                <CardDescription>Your invoices and payment history</CardDescription>
              </CardHeader>
              <CardContent>
                {invoices.length > 0 ? (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <div 
                        key={invoice.invoice_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                            <p className="text-sm text-slate-500">
                              {invoice.plan_name} • {invoice.billing_cycle} • {new Date(invoice.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold">{formatIndianCurrency(invoice.total_amount / 100)}</p>
                            {getInvoiceStatusBadge(invoice.status)}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => downloadInvoice(invoice.invoice_id)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No invoices yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Payment Security Notice */}
        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
          <Shield className="w-4 h-4" />
          <span>Secure payments via Razorpay or Bank Transfer</span>
        </div>

        {/* Trial confirmation - trial starts only after Confirm */}
        <PlanConfirmDialog
          open={showTrialDialog}
          onOpenChange={setShowTrialDialog}
          plan={selectedPlan}
          mode="trial"
          trialDays={selectedTrialDuration}
          onConfirm={handleStartTrial}
          loading={!!processingPlan}
        >
          {selectedPlan?.trial_durations?.length > 1 && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Trial Duration</label>
              <div className="flex gap-2">
                {selectedPlan.trial_durations.map((days) => (
                  <Button
                    key={days}
                    variant={selectedTrialDuration === days ? 'default' : 'outline'}
                    onClick={() => setSelectedTrialDuration(days)}
                    className="flex-1"
                    data-testid={`trial-duration-${days}`}
                  >
                    {days} Days
                  </Button>
                ))}
              </div>
            </div>
          )}
        </PlanConfirmDialog>

        {/* Paid plan confirmation - submission happens only after Confirm */}
        <PlanConfirmDialog
          open={showPaidConfirm}
          onOpenChange={(o) => { setShowPaidConfirm(o); if (!o && !submittingPayment) setShowPaymentDialog(true); }}
          plan={selectedPlan}
          mode="paid"
          billingCycle={billingCycle}
          onConfirm={handleSubmitPayment}
          loading={submittingPayment}
        />

        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Complete Your Subscription</DialogTitle>
              <DialogDescription>
                {selectedPlan && (
                  <>{selectedPlan.name} Plan • {formatIndianCurrency(getPrice(selectedPlan) / 100)} / {billingCycle}</>
                )}
              </DialogDescription>
            </DialogHeader>

            {/* Payment Method Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
              <button
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
                  paymentMethod === 'online' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setPaymentMethod('online')}
              >
                <Monitor className="w-4 h-4" />
                Pay Online
              </button>
              <button
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
                  paymentMethod === 'offline' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setPaymentMethod('offline')}
              >
                <Banknote className="w-4 h-4" />
                Pay Offline
              </button>
            </div>

            <div className="py-2 space-y-3">
              {paymentMethod === 'online' ? (
                <div className="text-center py-2">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <CreditCard className="w-6 h-6 text-indigo-600" />
                  </div>
                  <p className="text-slate-600 mb-2 text-sm">
                    Pay securely via Razorpay
                  </p>
                  {selectedPlan && (
                    <div className="bg-slate-50 p-2 rounded-lg text-left space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500 text-xs">Plan</span>
                        <span className="font-medium text-xs">{selectedPlan.name} ({billingCycle})</span>
                      </div>
                      <Separator className="my-0.5" />
                      <div className="flex justify-between">
                        <span className="text-slate-500 text-xs">Subtotal</span>
                        <span className="text-xs">{formatIndianCurrency(getPrice(selectedPlan) / 100)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 text-xs">GST (18%)</span>
                        <span className="text-xs">{formatIndianCurrency(Math.round(getPrice(selectedPlan) * 0.18) / 100)}</span>
                      </div>
                      <div className="flex justify-between font-semibold pt-1 border-t">
                        <span>Total</span>
                        <span>{formatIndianCurrency(Math.round(getPrice(selectedPlan) * 1.18) / 100)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Bank Details */}
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Banknote className="w-4 h-4 text-slate-600" />
                      <span className="font-semibold text-sm">Bank Transfer Details</span>
                    </div>
                    
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Bank</span>
                        <span className="font-medium">{BANK_DETAILS.bank}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Account Name</span>
                        <span className="font-medium text-right">{BANK_DETAILS.accountName}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Account No.</span>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{BANK_DETAILS.accountNo}</span>
                          <button onClick={() => copyToClipboard(BANK_DETAILS.accountNo)} className="p-0.5">
                            <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">IFSC Code</span>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{BANK_DETAILS.ifscCode}</span>
                          <button onClick={() => copyToClipboard(BANK_DETAILS.ifscCode)} className="p-0.5">
                            <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">UPI ID</span>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{BANK_DETAILS.upiId}</span>
                          <button onClick={() => copyToClipboard(BANK_DETAILS.upiId)} className="p-0.5">
                            <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Form */}
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Payment Method</Label>
                      <select
                        className="w-full mt-1 h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                        value={paymentForm.payment_method || 'bank_transfer'}
                        onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                      >
                        <option value="bank_transfer">Bank Transfer (NEFT/IMPS/RTGS)</option>
                        <option value="upi">UPI</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Reference / UTR No. *</Label>
                        <Input
                          placeholder="Transaction reference"
                          data-testid="payment-reference-input"
                          value={paymentForm.reference_no}
                          onChange={(e) => setPaymentForm({...paymentForm, reference_no: e.target.value})}
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Payment Date</Label>
                        <Input
                          type="date"
                          value={paymentForm.payment_date}
                          onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                          className="mt-1 h-9"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex-col gap-1.5 sm:flex-col">
              <Button 
                variant="secondary"
                className="w-full"
                onClick={() => { setShowPaymentDialog(false); setShowPaidConfirm(true); }}
                disabled={submittingPayment || (paymentMethod === 'offline' && !paymentForm.reference_no)}
                data-testid="payment-continue-btn"
              >
                {submittingPayment ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : paymentMethod === 'online' ? (
                  <><CreditCard className="w-4 h-4 mr-2" />Pay Now</>
                ) : (
                  <><Receipt className="w-4 h-4 mr-2" />Submit Payment Details</>
                )}
              </Button>
              {paymentMethod === 'offline' && (
                <p className="text-[10px] text-slate-500 text-center">
                  Subscription activated within 24-48 hrs after verification
                </p>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Success Dialog */}
        <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
          <DialogContent className="text-center">
            <DialogHeader>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <DialogTitle className="text-2xl">Trial Started!</DialogTitle>
              <DialogDescription className="text-base">
                Your {selectedTrialDuration}-day trial has been activated. Enjoy all the premium features!
              </DialogDescription>
            </DialogHeader>
            <Button 
              className="w-full mt-4"
              onClick={() => {
                setShowSuccessDialog(false);
                navigate(`/${tenantSlug}/dashboard`);
              }}
            >
              Go to Dashboard
            </Button>
          </DialogContent>
        </Dialog>

        {/* Features Dialog */}
        <Dialog open={showFeaturesDialog} onOpenChange={setShowFeaturesDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{featuresDialogPlan?.name} Plan - All Features</DialogTitle>
              <DialogDescription>
                Complete list of features included in this plan
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto py-4">
              <ul className="space-y-3">
                {featuresDialogPlan?.features?.map((feature, idx) => {
                  const Icon = FEATURE_ICONS[feature.feature_key] || Check;
                  return (
                    <li key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{feature.name}</p>
                        {feature.limit_value !== null && feature.limit_value !== -1 && (
                          <p className="text-sm text-slate-500">Limit: {feature.limit_value}</p>
                        )}
                        {feature.limit_value === -1 && (
                          <p className="text-sm text-green-600">Unlimited</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFeaturesDialog(false)}>
                Close
              </Button>
              <Button 
                onClick={() => {
                  setShowFeaturesDialog(false);
                  openPaymentDialog(featuresDialogPlan);
                }}
              >
                Subscribe to {featuresDialogPlan?.name}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SubscriptionPage;
